import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormBySchema, FormFieldLayout, Id, OptionsAC, useAPIWrapper } from 'drf-react-by-schema';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from 'src/app/hooks';
import { appDispatch, getDecimal } from 'src/utils/utils';
import { setFormIsValid, setHandleSubmit, setPage } from 'src/features/pageSlice';
import { FlocosProductionStatus, GENERAL_ADMINS } from 'src/constants';

import { Layout } from 'src/styles';
import { Divider, Typography } from '@mui/material';
import Box from '@mui/system/Box';
import { useHasPermissionsMulti } from 'src/hooks/useHasPermissions';

const MODEL = 'flocos_production';

export default function FlocosReviewForm() {
  const { id, type, formValues, perfil } = useAppSelector((state) => state.page);
  const navigate = useNavigate();
  const { pageForm, loadSinglePageData, onEditModel, getGenericModelList } = useAPIWrapper();
  const [isAdmin] = useHasPermissionsMulti([GENERAL_ADMINS]);
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid },
    register,
    watch,
  } = useForm({
    mode: 'onBlur',
    resolver: pageForm?.validationSchema ? yupResolver(pageForm.validationSchema) : undefined,
  });

  const [optionsAC, setOptionsAC] = useState<OptionsAC>();
  const responsavelEntrepostoRevisao = watch('responsavel_entreposto_revisao');
  const entreposto = watch('entreposto');

  const perfilEntreposto = useMemo(() => perfil?.entreposto, [perfil?.entreposto]);

  const hiddenFields = useMemo(() => {
    const newHiddenFields = ['id', 'status'];
    if (perfilEntreposto) {
      newHiddenFields.push('entreposto');
    }
    return newHiddenFields;
  }, [perfilEntreposto]);

  const fieldsLayout = useMemo(() => {
    const newFieldsLayout: FormFieldLayout[] = [
      {
        rows: [
          ...(perfilEntreposto ? [] : ['entreposto']),
          'data_revisao',
          'peso_flocos_revisao',
          'responsavel_grupo_revisao',
          'responsavel_entreposto_revisao',
        ],
      },
      {
        CustomElement: <Divider />,
      },
      {
        title: 'Observações',
        rows: ['observacoes_revisao', 'comentarios_revisao'],
      },
      {
        CustomElement: <Divider />,
      },
      {
        title: 'Recibo assinado',
        rows: ['foto_revisao'],
      },
    ];
    return newFieldsLayout;
  }, [perfilEntreposto]);

  const autoFocusField = useMemo(
    () => (perfilEntreposto ? 'peso_flocos_revisao' : 'entreposto'),
    [perfilEntreposto]
  );

  useEffect(() => {
    if ((!perfilEntreposto && !isAdmin) || !id) {
      return;
    }
    const loadAllDataAsync = async () => {
      const pageTitle = 'Revisão da produção de flocos';

      const values = await loadSinglePageData({
        model: MODEL,
        objId: id,
        fieldsLayout,
      });

      if (values) {
        if (values.status.value === FlocosProductionStatus.IN_PRODUCTION) {
          console.log('this production can not be reviewed!! Check why user found it');
          navigate('/flocos_production');
          return;
        }

        const newValues = {
          ...values,
          status: {
            ...values.status,
            value: FlocosProductionStatus.REVIEWED,
          },
          peso_flocos_revisao: values.peso_flocos_seco,
          ...(perfilEntreposto && {
            entreposto: perfilEntreposto,
            responsavel_entreposto_revisao: perfil,
          }),
          data_revisao: new Date(),
        };

        reset(newValues);

        appDispatch(
          setPage({
            model: MODEL,
            title: pageTitle,
            formValues: newValues,
            submitSuccessTarget: `/flocos_production/view/${id}/?type=reviewed`,
          })
        );
      }
    };
    loadAllDataAsync();
  }, [id, perfilEntreposto, perfil, fieldsLayout, isAdmin]);

  useEffect(() => {
    async function populateOptionsAC() {
      if ((!perfilEntreposto && !isAdmin) || !formValues) {
        return;
      }
      const newOptionsAC: OptionsAC = {};
      const values = { ...formValues };

      if (values.entreposto?.grupos_fornecedores) {
        const resultGrupo = await getGenericModelList({
          model: 'perfil',
          columnFields: ['id', 'label'],
          queryParams: [
            `grupo__in=${values.entreposto.grupos_fornecedores.map(({ id }: { id: Id }) => id).join(',')}`,
            '&ordering=nome',
          ],
        });
        if (resultGrupo) {
          newOptionsAC.responsavel_grupo_revisao = resultGrupo.data;
        }
      }

      if (perfilEntreposto || values.entreposto) {
        const entreposto = perfilEntreposto || values.entreposto;
        if (entreposto) {
          const resultEntreposto = await getGenericModelList({
            model: 'entreposto',
            id: entreposto.id,
            relatedModel: 'perfil',
            columnFields: ['id', 'label'],
          });
          if (resultEntreposto) {
            newOptionsAC.responsavel_entreposto_revisao = resultEntreposto.data;
          }
        }
      }

      setOptionsAC(newOptionsAC);
    }

    populateOptionsAC();
  }, [perfilEntreposto, formValues, isAdmin]);

  useEffect(() => {
    if (!responsavelEntrepostoRevisao) {
      console.log('responsavelEntrepostoRevisao OFF');
    } else {
      console.log('responsavelEntrepostoRevisao ON');
    }
  }, [responsavelEntrepostoRevisao]);

  useEffect(() => {
    if (!entreposto) {
      setValue('responsavel_entreposto_revisao', null);
    } else {
      console.log('entreposto ON');
    }
  }, [entreposto]);

  useEffect(() => {
    appDispatch(
      setPage({
        formDisabled: !isDirty,
      })
    );
  }, [isDirty]);

  useEffect(() => {
    appDispatch(setFormIsValid(isValid));
  }, [isValid]);

  useEffect(() => {
    appDispatch(setHandleSubmit(handleSubmit));
  }, [handleSubmit]);

  if (!pageForm?.schema || !formValues || !type || !fieldsLayout || !optionsAC) {
    return null;
  }

  return (
    <Box>
      <Typography sx={{ color: (theme) => theme.palette.primary.main, mt: 2 }}>
        Peso informado no lote
      </Typography>
      <Typography variant="h3" sx={{ color: (theme) => theme.palette.primary.main, mt: 1 }}>
        {getDecimal(getValues('peso_flocos_seco'), 'Kg')}
      </Typography>
      <Typography variant="body2" sx={{ mt: 2 }}>
        Lote sendo revisado: {getValues('lote')}
      </Typography>
      <FormBySchema
        fieldsLayout={fieldsLayout}
        autoFocusField={autoFocusField}
        hiddenFields={hiddenFields}
        control={control}
        register={register}
        errors={errors}
        schema={pageForm.schema}
        onEditModel={onEditModel}
        getValues={getValues}
        setValue={setValue}
        optionsAC={optionsAC}
        elevation={0}
        {...Layout.formLayout}
        sxSection={{ margin: 0 }}
      />
    </Box>
  );
}
